import React, { useState } from 'react';
import { Form, Input, Select, Button, message, Icon } from 'antd';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import { getUserInfoByEmail, updateUserCountry } from '../../axios';
import { storageGetItem } from '../../utils/index';
import './index.less';

const { Option } = Select;

function UpdateCountry(props) {
    const { t, form } = props;
    const [countryList, setCountryList] = useState([]);
    const [countryLoading, setCountryLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [userInfo, setUserInfo] = useState(null); // Store userId and region
    const [emailChecking, setEmailChecking] = useState(false);
    const [emailError, setEmailError] = useState(''); // Store email validation error
    const [lastCheckedEmail, setLastCheckedEmail] = useState(''); // Track last checked email

    // Fetch all countries
    const fetchCountries = async () => {
        setCountryLoading(true);
        try {
            const casApiUrl = storageGetItem(localStorage, 'serverConfig') 
                ? JSON.parse(storageGetItem(localStorage, 'serverConfig')).casServerUrl 
                : '';
            
            const url = `${casApiUrl}/api/common/country`;
            
            const res = await axios.get(url);
            const countryData = res.data.data;
            let list = [];
            if (Array.isArray(countryData)) {
                list = countryData;
            } else if (countryData && Array.isArray(countryData.countryList)) {
                list = countryData.countryList;
            }
            setCountryList(list);
        } catch (e) {
            message.error(t('FailedToLoadCountries'));
            setCountryList([]);
        }
        setCountryLoading(false);
    };

    // Handle email input blur event
    const handleEmailBlur = async (e) => {
        const email = e.target.value.trim();
        if (!email) {
            return;
        }
        
        // Skip if email hasn't changed
        if (email === lastCheckedEmail) {
            return;
        }
        
        // Validate email format first
        const emailReg = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!emailReg.test(email)) {
            return; // Let form validation handle this
        }
        
        setEmailChecking(true);
        setEmailError(''); // Clear previous error
        try {
            // Get user info by email
            const res = await getUserInfoByEmail(email);
            if (res.code === 100000 && res.data) {
                const userData = {
                    userId: res.data.id,
                    region: res.data.region || '' // May be empty
                };
                setUserInfo(userData);
                setEmailError(''); // Clear error on success
                setLastCheckedEmail(email); // Remember this email
                
                // Fetch all countries (region and country are decoupled)
                await fetchCountries();
            } else {
                setEmailError(t('UserNotFound'));
                setUserInfo(null);
                setCountryList([]);
                setLastCheckedEmail(email); // Remember failed email too
                form.setFieldsValue({ country: undefined });
            }
        } catch (error) {
            setEmailError(t('FailedToGetUserInfo'));
            setUserInfo(null);
            setCountryList([]);
            setLastCheckedEmail(email); // Remember failed email too
            form.setFieldsValue({ country: undefined });
        }
        setEmailChecking(false);
    };

    // Handle form submission
    const handleSubmit = e => {
        e.preventDefault();
        form.validateFields(async (err, values) => {
            if (!err) {
                // Check if user info is loaded
                if (!userInfo || !userInfo.userId) {
                    message.error(t('PleaseEnterValidEmail'));
                    return;
                }
                
                setSubmitLoading(true);
                try {
                    // Update user country with userId from state
                    const updateRes = await updateUserCountry({
                        userId: userInfo.userId,
                        country: values.country
                    });
                    
                    if (updateRes.code === 100000) {
                        message.success(t('UpdateSuccess'));
                        form.resetFields();
                        setUserInfo(null);
                        setCountryList([]);
                        setEmailError('');
                        setLastCheckedEmail(''); // Reset on success
                    } else {
                        message.error(updateRes.msg || t('UpdateFailed'));
                    }
                    setSubmitLoading(false);
                } catch (error) {
                    message.error(t('UpdateFailed'));
                    setSubmitLoading(false);
                }
            }
        });
    };

    const { getFieldDecorator } = form;
    
    return (
        <div className="main-new">
            <div className="main-cotent update-country-page">
                <div className='form-section-single'>
                    <h2>{t('UpdateCountryInfo')}</h2>
                    <Form
                        layout="vertical"
                        onSubmit={handleSubmit}
                        hideRequiredMark={false}
                    >
                        <Form.Item
                            label={t('Email')}
                            required
                            validateStatus={emailError ? 'error' : undefined}
                            help={emailError || undefined}
                        >
                            {getFieldDecorator('email', {
                                rules: [
                                    { required: true, message: t('PleaseEnterEmail') },
                                    { type: 'email', message: t('EmailFormatError') }
                                ],
                            })(
                                <Input 
                                    placeholder={t('PleaseEnterEmail')} 
                                    onBlur={handleEmailBlur}
                                    onChange={() => {
                                        // Only clear our custom error, not form validation
                                        if (emailError) {
                                            setEmailError('');
                                            setUserInfo(null);
                                            setCountryList([]);
                                            setLastCheckedEmail(''); // Reset to allow re-check
                                        }
                                    }}
                                    suffix={emailChecking ? <Icon type="loading" /> : null}
                                />
                            )}
                        </Form.Item>
                        
                        <Form.Item
                            label={t('Country')}
                            required
                        >
                            {getFieldDecorator('country', {
                                rules: [
                                    { required: true, message: t('PleaseSelectCountry') }
                                ],
                            })(
                                <Select 
                                    loading={countryLoading} 
                                    placeholder={t('PleaseSelectCountry')}
                                    disabled={!userInfo || countryList.length === 0}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {countryList.map(c => (
                                        <Option key={c.code} value={c.code}>{c.name}</Option>
                                    ))}
                                </Select>
                            )}
                        </Form.Item>
                        
                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={submitLoading}>
                                {t('Submit')}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </div>
    );
}

export default withTranslation(['common'])(Form.create()(UpdateCountry));
